import clsx from "clsx";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGetChat } from "~/api/chat/chat";
import { useGetCurrentUser } from "~/api/users/users";
import { DocAreaHeader } from "~/features/chat/components/docList/DocAreaHeader";
import { DocList } from "~/features/chat/components/docList/DocList";
import { DocSidebar } from "~/features/chat/components/docSidebar/DocSidebar";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { ROUTES, useChatDocumentParams } from "~/helpers";
import { useSkyViewStore } from "../store";
import { DocViewer } from "./docViewer";
import { PageSkeleton } from "./PageSkeleton";

export const SkyView = () => {
  const isDocSidebarExpanded = useSkyViewStore((state) => state.isExpanded);
  const navigate = useNavigate();
  const { organizationId, caseId, chatId, isRestricted, isGuest } =
    useChatDocumentParams();
  const { data: currentUser } = useGetCurrentUser();

  const { error: chatError } = useGetChat(organizationId, caseId, chatId, {
    query: {
      enabled: isGuest,
      retry: false,
    },
  });

  useEffect(() => {
    if (isGuest && chatError && (chatError as any).response?.status === 403) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [isGuest, chatError, navigate]);

  const handleNavigateBack = () => {
    navigate(ROUTES.CASE({ caseId, organizationId }));
  };

  const { pageItems, docList, isDocListLoading } = useDocList();

  if (!currentUser) return null;

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <div
        className={clsx("grid grid-cols-2 flex-1 min-w-0", {
          "w-full": !isDocSidebarExpanded,
          "w-3/4": isDocSidebarExpanded,
        })}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="h-[60px] flex-shrink-0">
            <DocAreaHeader onNavigateBack={handleNavigateBack} />
          </div>
          <div className="flex-1 overflow-hidden">
            <DocList />
          </div>
        </div>
        <div className="overflow-hidden">
          {isDocListLoading || !docList ? (
            <div className="flex flex-col items-center pt-3 px-3 gap-3 bg-gray-100 h-full">
              <PageSkeleton className="animate-pulse" />
              <PageSkeleton className="animate-pulse" />
            </div>
          ) : (
            <DocViewer
              pageItems={pageItems}
              docList={docList}
              isViewOnly={isRestricted}
            />
          )}
        </div>
      </div>
      <div
        className={clsx("flex-shrink-0 overflow-hidden", {
          "w-1/4": isDocSidebarExpanded,
          "w-auto": !isDocSidebarExpanded,
        })}
      >
        <DocSidebar isGuest={isGuest} />
      </div>
    </div>
  );
};
